import {
  AnchorType,
  CreateScheduleRequestDTO,
  FixedTriggerType,
  GetScheduleByIdResponseDTO,
  getWeekdayOccurrenceInMonth,
  UpdateScheduleRequestDTO,
} from '@gearbelt/global';
import { validate as isUuid } from 'uuid';
import { z } from 'zod';

// Special time-based units (days/weeks/months) use calendarInterval instead of unitId.
export const SPECIAL_TIME_UNITS = [
  { id: 'days', name: 'Days', abbreviation: 'd' },
  { id: 'weeks', name: 'Weeks', abbreviation: 'w' },
  { id: 'months', name: 'Months', abbreviation: 'm' },
];

export const isCalendarUnit = (unitId: string): boolean => SPECIAL_TIME_UNITS.some((u) => u.id === unitId);

function getOrdinalSuffix(day: number): string {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
}

function getWeekdayName(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'long' });
}

function getWeekdayOccurrence(date: Date): { occurrence: number; isLast: boolean } {
  const day = date.getDate();
  const occurrence = getWeekdayOccurrenceInMonth(date);

  // Check if this is the last occurrence of this weekday in the month
  const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
  const isLast = day + 7 > lastDayOfMonth;

  return { occurrence, isLast };
}

function getOccurrenceOrdinal(n: number): string {
  switch (n) {
    case 1:
      return '1st';
    case 2:
      return '2nd';
    case 3:
      return '3rd';
    case 4:
      return '4th';
    case 5:
      return '5th';
    default:
      return `${n}th`;
  }
}

// The "Repeats" options for a monthly fixed trigger, derived from the base date.
// `on-day` is only offered for days 1–28 because later days don't exist in every
// month; the first entry is the sensible auto-populate default.
export function getFixedTypeOptions(date: Date): { label: string; value: FixedTriggerType }[] {
  const options: { label: string; value: FixedTriggerType }[] = [];
  const dayOfMonth = date.getDate();
  const weekdayName = getWeekdayName(date);
  const { occurrence, isLast } = getWeekdayOccurrence(date);

  if (dayOfMonth <= 28) {
    options.push({
      label: `On the ${dayOfMonth}${getOrdinalSuffix(dayOfMonth)} of the selected months`,
      value: FixedTriggerType.OnDay,
    });
  }

  if (occurrence <= 4) {
    options.push({
      label: `On the ${getOccurrenceOrdinal(occurrence)} ${weekdayName} of the selected months`,
      value: FixedTriggerType.OnNthWeekday,
    });
  }

  if (isLast || occurrence >= 4) {
    options.push({
      label: `On the last ${weekdayName} of the selected months`,
      value: FixedTriggerType.OnLastWeekday,
    });
  }

  return options;
}

const triggerSchema = z
  .object({
    id: z.string(),
    interval: z.number().min(1, 'Interval is required').max(1000000, 'Interval cannot exceed 1,000,000'),
    unitId: z.string().min(1, 'Unit is required'),
    type: z.enum(['crawling', 'fixed']),
    startingFrom: z.string().optional(),
    fixedType: z.enum(FixedTriggerType).optional(),
  })
  .refine(
    (data) => {
      // startingFrom is only required for fixed calendar triggers
      if (data.type !== 'fixed') return true;
      if (!isCalendarUnit(data.unitId)) return true;
      return typeof data.startingFrom === 'string' && data.startingFrom.length > 0;
    },
    {
      message: 'Starting from is required',
      path: ['startingFrom'],
    },
  );

export const scheduleFormSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  triggers: z.array(triggerSchema).min(1, 'Add at least one trigger').max(5, 'Maximum 5 triggers allowed'),
  checks: z
    .array(
      z.object({
        id: z.string(),
        label: z.string().min(1, 'Label is required'),
        formId: z.string().min(1, 'Select one schedule form per check'),
      }),
    )
    .min(1, 'Add at least one check'),
  cycle: z
    .array(
      z.object({
        id: z.string(),
        checkId: z.string().min(1, 'Select one check'),
      }),
    )
    .min(1, 'Select one check'),
});

export type ScheduleFormData = z.infer<typeof scheduleFormSchema>;

export function parseCalendarInterval(calendarInterval: string): { interval: number; unitId: string } | null {
  // Parse format like '3d', '1w', '3m' into interval and unit
  const match = calendarInterval.match(/^(\d+)([dwm])$/);
  if (!match) return null;
  const interval = parseInt(match[1], 10);
  const abbreviation = match[2];
  const unit = SPECIAL_TIME_UNITS.find((u) => u.abbreviation === abbreviation);
  if (!unit) return null;
  return { interval, unitId: unit.id };
}

export function mapApiToForm(schedule: GetScheduleByIdResponseDTO['schedule']): ScheduleFormData {
  return {
    name: schedule.name,
    triggers:
      schedule.triggers?.map((t) => {
        // Parse calendarInterval if it exists (for special time units)
        const parsedCalendar = t.calendarInterval ? parseCalendarInterval(t.calendarInterval) : null;
        return {
          id: t.id,
          interval: parsedCalendar?.interval ?? t.unitInterval ?? 1,
          unitId: parsedCalendar?.unitId ?? t.unitId ?? '',
          type: (t.anchorType as 'crawling' | 'fixed') || 'crawling',
          startingFrom: t.baseDate || undefined,
          fixedType: t.fixedTriggerType ?? undefined,
        };
      }) || [],
    checks:
      schedule.checks?.map((c) => ({
        id: c.id,
        label: c.label,
        formId: c.formId,
      })) || [],
    cycle: [...(schedule.cycles ?? [])]
      .sort((a, b) => a.position - b.position)
      .map((cy) => ({
        id: cy.id,
        checkId: cy.scheduleCheckId,
      })),
  };
}

export function mapFormToApi(data: ScheduleFormData): CreateScheduleRequestDTO {
  return {
    name: data.name,
    triggers: data.triggers.map((t) => {
      const specialUnit = SPECIAL_TIME_UNITS.find((u) => u.id === t.unitId);
      const isSpecialTimeUnit = !!specialUnit;
      return {
        // For special time units (days/weeks/months), use calendarInterval instead of unitId
        // Format: '3d', '1w', '3m' (interval + abbreviation)
        unitId: isSpecialTimeUnit ? null : t.unitId,
        calendarInterval: isSpecialTimeUnit ? `${t.interval}${specialUnit.abbreviation}` : null,
        unitInterval: isSpecialTimeUnit ? null : t.interval,
        anchorType: t.type as AnchorType,
        baseDate: t.type === 'fixed' && isSpecialTimeUnit ? t.startingFrom : null,
        fixedTriggerType: t.type === 'fixed' && t.unitId === 'months' ? (t.fixedType ?? null) : null,
      };
    }),
    checks: data.checks.map((c) => ({
      label: c.label,
      formId: c.formId,
    })),
    cycles: data.cycle.map((cy, idx) => {
      const check = data.checks.find((c) => c.id === cy.checkId);
      return {
        position: idx,
        checkLabel: check?.label || '',
      };
    }),
  };
}

export function mapFormToUpdateApi(data: ScheduleFormData): UpdateScheduleRequestDTO['data'] {
  return {
    name: data.name,
    triggers: data.triggers.map((t) => {
      const specialUnit = SPECIAL_TIME_UNITS.find((u) => u.id === t.unitId);
      const isSpecialTimeUnit = !!specialUnit;
      // Only include id if it's a valid UUID (existing database record).
      // New items have temporary IDs like 'new-trigger-1234567890-abc123' which fail UUID validation,
      // signaling to the backend that this is a new record to insert rather than an existing one to update.
      const isExistingItem = t.id && isUuid(t.id);
      return {
        id: isExistingItem ? t.id : undefined,
        unitId: isSpecialTimeUnit ? null : t.unitId,
        calendarInterval: isSpecialTimeUnit ? `${t.interval}${specialUnit.abbreviation}` : null,
        unitInterval: isSpecialTimeUnit ? null : t.interval,
        anchorType: t.type as AnchorType,
        baseDate: t.type === 'fixed' && isSpecialTimeUnit ? t.startingFrom : null,
        fixedTriggerType: t.type === 'fixed' && t.unitId === 'months' ? (t.fixedType ?? null) : null,
      };
    }),
    checks: data.checks.map((c) => {
      // Only include id if it's a valid UUID (existing item)
      const isExistingItem = c.id && isUuid(c.id);
      return {
        id: isExistingItem ? c.id : undefined,
        label: c.label,
        formId: c.formId,
      };
    }),
    cycles: data.cycle.map((cy, idx) => {
      const check = data.checks.find((c) => c.id === cy.checkId);
      // Only include cycle id if it's a valid UUID (existing database record)
      const isExistingCycle = cy.id && isUuid(cy.id);
      // Check if the referenced check is an existing database record
      const isExistingCheck = check?.id && isUuid(check.id);

      // Discriminated union: send either scheduleCheckId (for existing checks) OR checkLabel (for new checks)
      if (isExistingCheck) {
        return {
          id: isExistingCycle ? cy.id : undefined,
          position: idx,
          scheduleCheckId: check.id,
        };
      } else {
        return {
          id: isExistingCycle ? cy.id : undefined,
          position: idx,
          checkLabel: check?.label || '',
        };
      }
    }),
  };
}
